import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Spin from 'antd/lib/spin';
import Modal from 'antd/lib/modal';

import { accountDocumentTypes } from '../../../common/enums';
import { selectToken } from '../../../features/auth/ducks';
import { selectIsInProgress } from '../../../features/spinner/ducks';

import {
  selectContracts,
  fetchAccountContractsActions,
  // loans (admin, used to discover PSD2 requisition/accounts)
  selectLoans,
  fetchAccountLoansActions,
} from '../ducks';

import { apiCallIds as filesApiCallIds } from '../../../features/files/api';
import {
  selectUploadedFilesById,
  selectUploadedFilesListById,
  uploadFileActions,
  removeUploadedFileAction,
  resetUploadedFilesAction,
} from '../../../features/files/ducks';

import {
  fetchAccountDocumentsActions,
  updateAccountDocumentsActions,
  setAccountDocumentTypeAction,
  removeAccountDocumentAction,
  selectDocuments,
  selectDocumentsVersion,
} from '../ducks';

import { apiCallIds as accountApiCallIds } from '../api';
import AccountDocuments from '../components/AccountDocuments';

// PSD2 ducks
import {
  fetchPsd2RequisitionActions,
  fetchPsd2IbansActions,
  fetchPsd2AccountDataActions,
  fetchPsd2AllTxActions,
  fetchPsd2CategoriesActions,
  fetchPsd2AggregationCriteriaActions,
  purgePsd2DataActions,
  clearPsd2RequisitionAccounts,
  selectPsd2Ibans,
  selectPsd2AccountData,
  selectPsd2Transactions,
  selectPsd2Categories,
  selectPsd2AggregationCriteria,
  selectPsd2RequisitionAccountIds,
} from '../../../features/psd2/ducks';
import { selectSetting } from '../../../features/settings/ducks';

import Psd2StatementsAdmin from '../components/psd2/Psd2StatementsAdmin';

const UPLOADED_FILES_ID = 'accountDocuments';

const mapStateToProps = state => ({
  contracts: selectContracts(state),
  documents: selectDocuments(state),
  documentsVersion: selectDocumentsVersion(state),
  isLoading: selectIsInProgress(state, accountApiCallIds.FETCH_ACCOUNT_DOCUMENTS),
  isUpdating: selectIsInProgress(state, accountApiCallIds.UPDATE_ACCOUNT_DOCUMENTS),
  isUploading: selectIsInProgress(state, filesApiCallIds.UPLOAD_FILE),
  token: selectToken(state),
  uploadedFiles: selectUploadedFilesById(state, UPLOADED_FILES_ID),
  uploadedFilesList: selectUploadedFilesListById(state, UPLOADED_FILES_ID),

  loans: selectLoans(state),

  // PSD2 state

  usePSD2Integration: selectSetting(state, 'PSD2_INTEGRATION_ON'),
  psd2AccountData: selectPsd2AccountData(state),
  psd2Transactions: selectPsd2Transactions(state),
  psd2Categories: selectPsd2Categories(state),
  psd2Criteria: selectPsd2AggregationCriteria(state),
  psd2AccountIds: selectPsd2RequisitionAccountIds(state),
  psd2Ibans: selectPsd2Ibans(state),
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      fetchAccountContracts: fetchAccountContractsActions.request,
      fetchAccountDocuments: fetchAccountDocumentsActions.request,
      removeAccountDocument: removeAccountDocumentAction,
      removeUploadedFile: removeUploadedFileAction,
      resetUploadedFiles: resetUploadedFilesAction,
      setAccountDocumentType: setAccountDocumentTypeAction,
      updateAccountDocuments: updateAccountDocumentsActions.request,
      uploadFile: uploadFileActions.request,

      // Loans (to obtain PSD2 requisition)
      fetchAccountLoans: fetchAccountLoansActions.request,

      // PSD2
      psd2FetchRequisition: fetchPsd2RequisitionActions.request,
      psd2FetchIbans: fetchPsd2IbansActions.request,
      psd2FetchAccountData: fetchPsd2AccountDataActions.request,
      psd2FetchAllTx: fetchPsd2AllTxActions.request,
      psd2FetchCategories: fetchPsd2CategoriesActions.request,
      psd2FetchCriteria: fetchPsd2AggregationCriteriaActions.request,
      psd2PurgeData: purgePsd2DataActions.request,
      psd2ClearAccounts: clearPsd2RequisitionAccounts,
    },
    dispatch
  ),
});

@connect(mapStateToProps, mapDispatchToProps)
export default class AccountDocumentsContainer extends Component {
  static propTypes = {
    account: PropTypes.object,
    accountId: PropTypes.string.isRequired,
    actions: PropTypes.object.isRequired,
    contracts: PropTypes.array.isRequired,
    documents: PropTypes.array.isRequired,
    documentsVersion: PropTypes.number.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isUpdating: PropTypes.bool.isRequired,
    isUploading: PropTypes.bool.isRequired,
    token: PropTypes.string.isRequired,
    uploadedFiles: PropTypes.array.isRequired,
    uploadedFilesList: PropTypes.array.isRequired,

    // PSD2
    loans: PropTypes.array.isRequired,
    psd2Ibans: PropTypes.array.isRequired, // [{accountId, IBAN}]
    psd2AccountData: PropTypes.object,
    psd2Transactions: PropTypes.array.isRequired,
    psd2Categories: PropTypes.array.isRequired,
    psd2Criteria: PropTypes.array.isRequired,
    psd2AccountIds: PropTypes.array.isRequired,
  };

  state = {
    modalVisible: false,
    documentId: null,
    showPsd2: false,
    selectedPsd2AccountId: undefined,
  };

  constructor(props) {
    super(props);
    this.props.actions.resetUploadedFiles(UPLOADED_FILES_ID);
  }

  componentDidMount() {
    const { actions, accountId } = this.props;
    actions.fetchAccountDocuments(accountId);
    actions.fetchAccountContracts(accountId);
    actions.fetchAccountLoans({ accountId });
  }

  getRequisitionsStatus = () => {
    const loans = this.props.loans || [];
    let hasDeleted = false;
    let hasStarted = false;

    loans.forEach(loan => {
      if (Array.isArray(loan.psd2Requisitions)) {
              if (loan.psd2Requisitions.some(r => r.system_status === 'deleted')){
          hasDeleted = true;
        }
        if (loan.psd2Requisitions.some(r => r.system_status === 'new' && r.status === 'CR')){
          hasStarted = true;
        }
      }
    })

    return {hasDeleted,hasStarted}
  }

  getAllRequisitionIds = () => {
    const loans = this.props.loans || [];
    const ids = new Set();
    for (let i = 0; i < loans.length; i++) {
      const l = loans[i] || {};
      if (Array.isArray(l.psd2Requisitions)) {
        l.psd2Requisitions.filter((r)=>(r.system_status !== 'deleted') && (r.status === 'LN' || r.status === 'EX')).forEach(r => r && r.id && ids.add(r.id));
      }
    }
    return Array.from(ids).filter(Boolean);
  };

  componentDidUpdate(prevProps) {
    const { accountId, loans, psd2AccountIds, actions } = this.props;

    if (accountId !== prevProps.accountId) {
      actions.fetchAccountDocuments(accountId);
      actions.fetchAccountContracts(accountId);
      actions.fetchAccountLoans({ accountId });
      actions.resetUploadedFiles(UPLOADED_FILES_ID);
      this.setState({ showPsd2: false, selectedPsd2AccountId: undefined });
    }

    if (loans !== prevProps.loans && Array.isArray(loans) && loans.length) {
      // collect ALL requisition ids across the user's loans
      let reqIds = this.getAllRequisitionIds();
      actions.psd2ClearAccounts();
      // dispatch fetch for each requisition
      for (let j = 0; j < reqIds.length; j++) {
        actions.psd2FetchRequisition(reqIds[j]);
      }
    }

    // when we get PSD2 account ids, pick the first and load data
    if (psd2AccountIds !== prevProps.psd2AccountIds && psd2AccountIds && psd2AccountIds.length) {
      const firstId =
        typeof psd2AccountIds[0] === 'string' ? psd2AccountIds[0] : psd2AccountIds[0];
      this.setState({ selectedPsd2AccountId: firstId });

      // Load IBAN translations for all account IDs (keeps shape: [{accountId, IBAN}])
      actions.psd2FetchIbans({ ids: psd2AccountIds });

      actions.psd2FetchCategories({});
      actions.psd2FetchCriteria({});
      actions.psd2FetchAccountData(firstId);
      actions.psd2FetchAllTx({ accountId: firstId, pageSize: 20000 });
    }
  }

  // ======= PSD2 purge helpers =======

  onPurgeAllPsd2ForAccount = () => {
    const requisitionIds = this.getAllRequisitionIds();
    if (!requisitionIds.length) {
      return Promise.resolve();
    }
    // fire-and-forget purge
    this.props.actions.psd2PurgeData({ requisitionIds });

    // Optimistically clear local PSD2 selection
    this.setState({ selectedPsd2AccountId: undefined,showPsd2: false });

    // Optionally, refresh loans (so UI reflects removed PSD2 data later)
    this.props.actions.fetchAccountLoans({ accountId: this.props.accountId });

    return Promise.resolve();
  };

  // ======= docs flow =======

  onUpload = uploadData => {
    const { uploadFile } = this.props.actions;
    const formData = new FormData();
    formData.append('dataFile', uploadData.file);
    uploadFile({
      uploadedFilesId: UPLOADED_FILES_ID,
      formData: formData,
    });
  };

  onSave = () => {
    const { uploadedFiles, documents, documentsVersion, accountId, actions } = this.props;
    actions.updateAccountDocuments({
      accountId: accountId,
      uploadedFilesId: UPLOADED_FILES_ID,
      documentsData: {
        documents: [
          ...documents.map(document => ({
            file: {
              id: document.file.id,
              name: document.file.name,
            },
            type: document.type,
          })),
          ...uploadedFiles.map(f => ({
            file: { id: f.id, name: f.name },
            type: accountDocumentTypes.OTHER,
          })),
        ],
        version: documentsVersion,
      },
    });
  };

  onCancel = () => {
    const { actions, accountId } = this.props;
    actions.fetchAccountDocuments(accountId);
    actions.resetUploadedFiles(UPLOADED_FILES_ID);
  };

  onUploadedFileRemove = file => {
    this.props.actions.removeUploadedFile({
      uploadedFilesId: UPLOADED_FILES_ID,
      fileId: file.uid,
    });
    return true;
  };

  onFileTypeChange = id => type => {
    this.props.actions.setAccountDocumentType({ id: id, type: type });
  };

  onModalConfirm = () => {
    this.props.actions.removeAccountDocument(this.state.documentId);
    this.onModalCancel();
  };

  onModalCancel = () => {
    this.setState({ modalVisible: false, documentId: null });
  };

  onAccountDocumentRemove = (id) => () => {
    this.setState({ modalVisible: true, documentId: id });
  };

  // ======= render =======

  render() {
    const {
      account,
      contracts,
      documents,
      isLoading,
      isUpdating,
      isUploading,
      token,
      uploadedFiles,
      uploadedFilesList,

      // PSD2 data
      usePSD2Integration,
      psd2Ibans,
      psd2AccountData,
      psd2Transactions,
      psd2Categories,
      psd2Criteria,
      psd2AccountIds,
    } = this.props;

    const { showPsd2, selectedPsd2AccountId } = this.state;

    if (!account) {
      return <Spin spinning />;
    }

    // PSD2 availability
    const reqStatus = this.getRequisitionsStatus();
    const psd2Available = (parseInt(usePSD2Integration) === 1) && (this.getAllRequisitionIds().length > 0 || reqStatus.hasDeleted || reqStatus.hasStarted);

    // doc split
    const documentUploadedByUser = documents.filter(d => d.file.accountId === this.props.accountId);
    const documentUploadedByAdmin = documents.filter(d => d.file.accountId !== this.props.accountId);

    if (showPsd2) {
      return (
        <Spin spinning={isLoading || isUpdating || isUploading}>
          <Psd2StatementsAdmin
            loading={isLoading || isUpdating || isUploading}
            accountIds={psd2AccountIds || []}
            selectedAccountId={selectedPsd2AccountId}
            ibans={psd2Ibans || []} // [{ accountId, IBAN }]
            accountData={psd2AccountData}
            transactions={psd2Transactions || []}
            categories={psd2Categories || []}
            criteria={psd2Criteria || []}
            hasAny={(psd2Transactions && psd2Transactions.length > 0) || false}

            // fx
            fetchAccountData={this.props.actions.psd2FetchAccountData}
            fetchAllTx={this.props.actions.psd2FetchAllTx}
            fetchCategories={this.props.actions.psd2FetchCategories}
            fetchCriteria={this.props.actions.psd2FetchCriteria}
            onChangeAccount={(id) => this.setState({ selectedPsd2AccountId: id })}

            // destructive hook: purge ALL requisitions for this account (across all its loans)
            onPurgeTriggered={this.onPurgeAllPsd2ForAccount}
          />
        </Spin>
      );
    }

    return (
      <Spin spinning={isLoading || isUpdating || isUploading}>
        <AccountDocuments
          accountRole={account.role}
          contracts={contracts}
          documentUploadedByAdmin={documentUploadedByAdmin}
          documentUploadedByUser={documentUploadedByUser}
          onAccountDocumentRemove={(id) => this.onAccountDocumentRemove(id)}
          onCancel={this.onCancel}
          onFileTypeChange={this.onFileTypeChange}
          onSave={this.onSave}
          onUpload={this.onUpload}
          onUploadedFileRemove={this.onUploadedFileRemove}
          token={token}
          uploadedFiles={uploadedFiles}
          uploadedFilesList={uploadedFilesList}
          psd2Available={psd2Available}
          loans={this.props.loans}
          accountId={account.id}
          onOpenPsd2={() => this.setState({ showPsd2: true })}
        />
        <span>
          <Modal
            title={'Naozaj si prajete vymazať dokument?'}
            visible={this.state.modalVisible}
            confirmLoading={isLoading || isUpdating || isUploading}
            onOk={this.onModalConfirm}
            onCancel={this.onModalCancel}
            okText={'Vymazať'}
          >
            {'Pre vymazanie dokumentu stlačte tlačidlo vymazať.'}
          </Modal>
        </span>
      </Spin>
    );
  }
}



// WEBPACK FOOTER //
// ./src/features/account-management/containers/AccountDocumentsContainer.js